// SwiperSlider.js
import StencilHTMLElement from "./stencil-element";

class SwiperSlider extends StencilHTMLElement {
  name = "SwiperSlider";
  instance;
  #options;

  constructor() {
    super();
  }

  // Public getter if you need to inspect current options
  get options() {
    return this.#options;
  }

  // Setter now treats value as a "patch" and merges it
  set options(value) {
    this.#applyOptionsPatch(value);
  }

  connectedCallback() {
    // Parse JSON options attribute if present (non-function bits only)
    if (!this.#options && this.hasAttribute("options")) {
      try {
        this.#options = JSON.parse(this.getAttribute("options") || "{}");
      } catch {
        // ignore parse errors
      }
    }

    // Always request function callbacks from the host app
    this.dispatchEvent(
      new CustomEvent("swiper-slider:configure", {
        bubbles: true,
        detail: {
          el: this,
          // Consumers call this with a *patch*. We handle merging.
          setOptions: (patch) => {
            this.#applyOptionsPatch(patch);
          }
        }
      })
    );

    this.init();
  }

  disconnectedCallback() {
    if (this.instance) {
      this.instance.destroy(true, true);
      this.instance = undefined;
    }
  }

  async init() {
    if (!this.isConnected) return;
  
    await window.Stencil.ensureVendor("swiper");
    if (!this.isConnected) return;
  
    if (this.instance) {
      this.instance.destroy(true, true);
      this.instance = undefined;
    }
  
    const base = this.#computeBaseFromAttributes();
    const opts = { ...base, ...(this.#options || {}) };
  
    console.log(this.logPrefix, `Initializing Swiper ${this.id} with options`, opts);

    // @ts-ignore
    // this.instance = new window.Swiper(this.firstElementChild, opts);
    // await window.Stencil.ensureVendor("swiper");

    const mods = window.SwiperModules;
    this.instance = new window.Swiper(this.firstElementChild, {
      modules: [mods.Navigation, mods.Pagination, mods.Autoplay, mods.Thumbs],
      ...opts,
    });

    this.#bindHorizontalEdgeFade();

    this.dispatchEvent(
      new CustomEvent("swiper-slider:ready", {
        bubbles: true,
        detail: { el: this, instance: this.instance }
      })
    );
  }

  /** Horizontal carousel edge gradients (see `carousel` snippet `edge_fade_enabled`). */
  #bindHorizontalEdgeFade() {
    if (this.getAttribute("edge-fade") !== "true") return;
    const fadeRoot = this.firstElementChild;
    if (!fadeRoot?.classList.contains("carousel-edge-fade")) return;
    const swiper = this.instance;
    if (!swiper) return;

    const ancestor = this.closest("[data-outer]");
    if (ancestor) {
      const bg = getComputedStyle(ancestor).backgroundColor;
      if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
        fadeRoot.style.setProperty("--edge-fade-bg", bg);
      }
    }

    const update = () => {
      fadeRoot.classList.toggle("has-scroll-left", !swiper.isBeginning);
      fadeRoot.classList.toggle("has-scroll-right", !swiper.isEnd);
    };
    update();
    swiper.on("progress", update);
    swiper.on("reachBeginning", update);
    swiper.on("reachEnd", update);
    swiper.on("fromEdge", update);
  }  

  #computeBaseFromAttributes() {
    const slideClass  = this.getAttribute("slide-class");
    const gutterClass = this.getAttribute("gutter-class");
    const navEnabled  = this.getAttribute("navigation") === "true";

    const opts = {
      resizeObserver: false,
      threshold: 20
    };

    if (navEnabled) {
      opts.navigation = {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev"
      };
    }

    if (slideClass || gutterClass) {
      opts.breakpoints = Stencil.getSwiperBreakpointsByColAndGutter(
        slideClass,
        gutterClass
      );
    } else {
      // Sensible base default
      opts.slidesPerView = 1;
      opts.spaceBetween  = 0;
    }

    return opts;
  }

  // Accepts a "patch" and merges it into existing options
  #applyOptionsPatch(patch) {
    if (!patch) return;

    const current = this.#options || {};
    const next = SwiperSlider.#mergeOptions(current, patch);

    this.#options = next;

    // Only re-init if Swiper already exists.
    // During first connect, `instance` is undefined, so we avoid double init.
    if (this.isConnected && this.instance) {
      this.init();
    }
  }

  // Shallow merge, with special handling for nested objects like `on` and `breakpoints`
  static #mergeOptions(prev, patch) {
    const out = {
      ...prev,
      ...patch
    };

    // Merge `on` handlers so you can add slideChange without nuking init, etc
    if (prev.on || patch.on) {
      out.on = {
        ...(prev.on || {}),
        ...(patch.on || {})
      };
    }

    // Optional: merge breakpoints in a similar way
    if (prev.breakpoints || patch.breakpoints) {
      out.breakpoints = {
        ...(prev.breakpoints || {}),
        ...(patch.breakpoints || {})
      };
    }

    return out;
  }
}

export default SwiperSlider;